const TIMEZONE = "Europe/Amsterdam";

const yearFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
});

/**
 * The calendar year `date` falls on in Netherlands local time (not UTC).
 * Mirrors api/src/lib/amsterdam-time.ts's amsterdamYear — duplicated rather than
 * shared since app/ and api/ are separate workspaces with no shared-lib package.
 * Accepts `string` too (vs. the api version's `Date`-only) since callers here pass
 * ISO date strings straight from API responses.
 */
export function amsterdamYear(date: Date | string): number {
  return Number(yearFormatter.format(new Date(date)));
}

const yearMonthFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
});

/** `date` as "YYYY-MM" in Netherlands local time, the key format of the monthly sessions rollup. */
export function amsterdamYearMonth(date: Date | string): string {
  return yearMonthFormatter.format(new Date(date));
}

const monthFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  month: "numeric",
});

/** Calendar month 1–12 in Netherlands local time. */
export function amsterdamMonth(date: Date | string = new Date()): number {
  return Number(monthFormatter.format(new Date(date)));
}

/** October–December in Netherlands local time. */
export function isAmsterdamQ4(now: Date = new Date()): boolean {
  return amsterdamMonth(now) >= 10;
}

const dayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** `date` as "YYYY-MM-DD" in Netherlands local time. */
export function amsterdamDay(date: Date | string | number): string {
  return dayFormatter.format(new Date(date));
}

/**
 * A TanStack `staleTime` for data the pipeline rewrites once a night: fresh for as long as
 * it was fetched on today's Netherlands calendar day, stale from midnight on. Evaluated
 * each time a component mounts the query, so the first visit of a new day refetches and
 * every other visit that day reuses the cache.
 */
export function freshForTheAmsterdamDay(query: {
  state: { dataUpdatedAt: number };
}): number {
  return amsterdamDay(query.state.dataUpdatedAt) === amsterdamDay(Date.now())
    ? Infinity
    : 0;
}
